import React, { useEffect, useState } from "react";
import { FiSearch } from "react-icons/fi";
import { siteRoutes } from "utils/helpers/enums/routes";
import { ResourceDTO } from "utils/helpers/models/shared/resource.dto";
import useHost from "containers/host/useHooks";
import { ROLES } from "utils/helpers/enums/roles.enum";
import ResourceCard from "components/cards/resource-card";
import { useSelector } from "react-redux";
import AmbassadorBreadcrumb, { AmbassadorBreadcrumbLink } from "components/breadcrumbs/AmbassadorBreadcrumb";
import { ResourceFilterDTO } from "utils/helpers/models/attendee/resource-filter.dto";
import { useDebounce } from "hooks/useDebounce";
import Pagination from "components/pagination";
import CustomMessageDisplay from "components/data-not-found/CustomMessageDisplay";
import noFoundGif from "assets/images/gifs/noFoundGif.gif";

const AttendeeResource: React.FC = () => {
  const breadcrumbLinks: AmbassadorBreadcrumbLink[] = [
    { title: "Home ", path: siteRoutes.ambassadorHome },
    { title: "Resource Library", path: siteRoutes.ambassadorResourceLibrary },
  ];
  const [activeTab, setActiveTab] = useState<string>("All Types");
  const [data, setData] = useState<ResourceDTO[]>([]);
  const [filters, setFilters] = useState<ResourceFilterDTO>(new ResourceFilterDTO({ 
    role: ROLES.AMBASSADOR,
    page: 0,
    size: 12
  }));
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const { getLibraryResources } = useHost();
  const { isLoading } = useSelector((state: any) => state.sharedReducer);

  useEffect(() => {
    getAllResources(filters);
  }, []);

  // Handle debounced search
  useEffect(() => {
    // Skip initial mount - initial load is handled by the first useEffect
    if (debouncedSearchTerm !== undefined) {
      const newFilters = {
        ...filters,
        title: debouncedSearchTerm ? debouncedSearchTerm.toLowerCase() : "",
        page: 0,
      };
      setFilters(newFilters);
      getAllResources(newFilters);
    }
  }, [debouncedSearchTerm]);

  const getAllResources = (filters: ResourceFilterDTO) => {
    // Pass false to replace data instead of appending
    getLibraryResources(data, setData, filters, setFilters, false);
    
    // Mark as fetched after first API call
    if (!hasFetched) {
      setHasFetched(true);
    }
  }

  const handlePageChange = (pageInfo: { selected: number; limit: number }) => {
    const newFilters = { 
      ...filters, 
      page: pageInfo.selected,
      size: pageInfo.limit 
    };
    setFilters(newFilters);
    getAllResources(newFilters);
  };

  const handleTabChange = (tab: string) => {
    // Reset data immediately to show loading state
    setData([]);
    
    // Update active tab
    setActiveTab(tab);
    
    // Create new filters with reset pagination
    const newFilters = {
      ...filters,
      tag: tab === "All Types" ? "" : tab,
      page: 0,
      size: 12,
    };
    
    setFilters(newFilters);
    getAllResources(newFilters);
  };

  return (
    <div className="container min-h-[100vh]">
      <AmbassadorBreadcrumb links={breadcrumbLinks} />
      <h2 className="text-2xl font-bold mb-2 sm:mb-4">Resource Library</h2>
      {/* Tabs */}
      <div className="flex flex-wrap justify-between gap-4 py-5">
        <div className="flex flex-wrap gap-4 sm:gap-8  pb-2">
          {[
            "All Types",
            "Guides",
            "Tips",
            "Instructions",
            "More Instructions",
            "Resource Library",
          ].map((tab) => (
            <button
              key={tab}
              className={`text-base font-semibold pb-1 ${activeTab === tab
                ? "text-lightBlueColor border-b-2 border-lightBlueColor"
                : "text-grayText hover:text-lightBlueColor"
                }`}
              onClick={() => handleTabChange(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
        <div className="relative">
          {/* Search Icon */}
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FiSearch className="h-5 w-5 text-gray-400" />{" "}
          </div>
          <input
            type="search"
            placeholder="Find Resource.."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor  outline-none"
          />
        </div>
      </div>
      {/* resourceeriences */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
        {data.map((resource, index) => <ResourceCard key={index} resource={resource} />)}
      </div>

      <CustomMessageDisplay
        show={!isLoading && !data.length && hasFetched}
        title={
          activeTab === "All Types"
            ? "No Resources Found"
            : activeTab === "Guides"
            ? "No Guides Available"
            : activeTab === "Tips"
            ? "No Tips Available"
            : activeTab === "Instructions"
            ? "No Instructions Available"
            : activeTab === "More Instructions"
            ? "No More Instructions Available"
            : activeTab === "Resource Library"
            ? "No Resource Library Items Available"
            : "No Resources Found"
        }
        slogan={
          activeTab === "All Types"
            ? "No resources available at the moment. Check back later for new content!"
            : activeTab === "Guides"
            ? "You don't have any guides yet. New guides will appear here when they're available."
            : activeTab === "Tips"
            ? "No tips available right now. Stay tuned for helpful tips and tricks!"
            : activeTab === "Instructions"
            ? "No instructions available at this time. New instructions will be added soon."
            : activeTab === "More Instructions"
            ? "No additional instructions available. Check back later for more content."
            : activeTab === "Resource Library"
            ? "No resource library items available. New resources will appear here when added."
            : "No resources available at the moment"
        }
        image={noFoundGif}
        imageAlt="No resources found"
        className="py-12"
        imageClassName="w-24 h-24"
        titleClassName="text-2xl font-bold text-blackie"
        sloganClassName="text-gray-500 text-base"
      />
      {data.length > 0 && filters.totalElements > filters.size && (
        <Pagination
          count={filters.totalElements}
          page={filters.page}
          limit={filters.size}
          onPageChange={handlePageChange}
          activeBgColor={"#37588A"}
          activeTextColor={"#ffffff"}
        />
      )}
   
    </div>
  );
};

export default AttendeeResource;
